import RESTClient from "../../../../clients/RESTClient";
import Secrets from "@yumi/secrets";
import utils from "../../../../lib/utils";
import { TaskStateEnumBreakAdjustment, TaskEnumBreakAdjustment, PendingSectionsResponse, SectionDetailResponse } from "../utils/";

class BreakAdjustmenAlertClient extends RESTClient {
    private readonly BreakAdjustmentAlertUrl = "break-adjustments";

    /**
     * Resuelve el prefijo de ruta dinámicamente según el rol activo del usuario.
     */
    private getRolePrefix(): string {
        if (utils.hasAccessByRole(['breakadjustment.business'])) return 'business/';
        if (utils.hasAccessByRole(['breakadjustment.manager'])) return 'manager/';
        if (utils.hasAccessByRole(['breakadjustment.submanager'])) return 'submanager/';
        return ''; // Para 'breakadjustment.tools' (vendedor / operador)
    }

    async getSectionList(country: string, store: string, taskType: TaskEnumBreakAdjustment, state?: TaskStateEnumBreakAdjustment): Promise<PendingSectionsResponse> {
        const prefix = this.getRolePrefix();
        const response = await this.axios.get(`/${this.BreakAdjustmentAlertUrl}/${prefix}pending/${country}/${store}`,
            {
                params: {
                    taskType,
                    state: state || TaskStateEnumBreakAdjustment.PENDING,
                }
            }
        );
        return response.data as PendingSectionsResponse;
    }

    async getProductBySection(country: string, store: string, sectionCode: string, state?: TaskStateEnumBreakAdjustment, taskType?: TaskEnumBreakAdjustment): Promise<SectionDetailResponse> {
        try {
            const prefix = this.getRolePrefix();
            const response = await this.axios.get(`/${this.BreakAdjustmentAlertUrl}/${prefix}pending/${country}/${store}/sections/${sectionCode}`, {
                params: {
                    state,
                    taskType,
                }
            });
            return response.data as SectionDetailResponse;
        } catch (error) {
            console.error('Error fetching product by section:', error);
            return { products: [] } as unknown as SectionDetailResponse;
        }
    }

    async filterProductsBySection(country: string, store: string, sectionCode: string): Promise<SectionDetailResponse> {
        const prefix = this.getRolePrefix();
        const response = await this.axios.get(`/${this.BreakAdjustmentAlertUrl}/${prefix}pending/${country}/${store}/sections/${sectionCode}`);
        return response.data as SectionDetailResponse;
    }

    async getProductBySectionAndSku(country: string, store: string, sectionCode: string, sku: string): Promise<SectionDetailResponse> {
        const prefix = this.getRolePrefix();
        const response = await this.axios.get(`/${this.BreakAdjustmentAlertUrl}/${prefix}pending/${country}/${store}/sections/${sectionCode}/${sku}`);
        return response.data as SectionDetailResponse;
    }

    async getStockReference(country: string, store: string, taskId: string): Promise<import('../utils/types').BreakStockReference> {
        const prefix = this.getRolePrefix();
        const response = await this.axios.get(`/${this.BreakAdjustmentAlertUrl}/${prefix}pending/${encodeURIComponent(country)}/${encodeURIComponent(store)}/${encodeURIComponent(taskId)}/stock-reference`);
        return response.data;
    }

    async newBreakAdjustmentAlertTask(country: string, store: string, metaData: any, taskId: string): Promise<any> {
        // La creación de tareas solo la realiza el vendedor (ruta base)
        const response = await this.axios.post(`/${this.BreakAdjustmentAlertUrl}/pending`, {
            store,
            country,
            metadata: metaData,
            taskId
        });
        return response.data;
    }

    async getTasksByState(country: string, store: string, states: string[]): Promise<any[]> {
        try {
            const stateQuery = states.join(',');
            const prefix = this.getRolePrefix();
            const response = await this.axios.get(`/${this.BreakAdjustmentAlertUrl}/${prefix}pending/${country}/${store}`, {
                params: {
                    states: stateQuery,
                    taskType: TaskEnumBreakAdjustment.BREAKADJUSTMENTALERTFORAPPROVE
                }
            });
            return response.data as any[];
        } catch (error) {
            console.error('Error fetching tasks by state:', error);
            return [];
        }
    }

    async updateBreakAdjustmentAlertTask(country: string, store: string, taskId: string, metaData: any): Promise<any> {
        const prefix = this.getRolePrefix();
        const response = await this.axios.put(`/${this.BreakAdjustmentAlertUrl}/${prefix}pending/${country}/${store}/${taskId}`, {
            metadata: metaData,
            taskId
        });
        return response.data;
    }

    async processBreakAdjustments(country: string, store: string, entries: Array<{ taskId: string; decision: 'APPROVED' | 'REJECTED'; quantity?: number; units?: number; stockReference?: import('../utils/types').BreakStockReference }>): Promise<any> {
        const prefix = this.getRolePrefix();
        const response = await this.axios.post(`/${this.BreakAdjustmentAlertUrl}/${prefix}pending/${country}/${store}/process`, { entries });
        return response.data;
    }

    async getExpiredTasks(country: string, store: string): Promise<any[]> {
        try {
            const prefix = this.getRolePrefix();
            const response = await this.axios.get(`/${this.BreakAdjustmentAlertUrl}/${prefix}expired/${country}/${store}`);
            return response.data as any[];
        } catch (error) {
            console.error('Error fetching expired tasks:', error);
            return [];
        }
    }
}

export default new BreakAdjustmenAlertClient({
    baseURL: Secrets.REACT_APP_API_ENDPOINT!,
});